/**
 * Utilities for detecting which form fields can be charted
 * Filters out PII and text fields
 */

import {
  DEMOGRAPHIC_FIELDS,
  DISTRICT_PARTS,
  AGE_RANGE_OPTIONS,
  PARTY_AFFILIATION_OPTIONS,
  GENDER_OPTIONS
} from '../constants/outreach';

export interface Question {
  id: string;
  label?: string;
  question?: string;
  text?: string;
  title?: string;
  shortLabel?: string;
  description?: string;
  type: string;
  options?: { value: string; label: string }[];
}

export interface ChartableField {
  id: string;
  label: string;
  shortLabel?: string;
  description?: string;
  suggestedChartTypes: ('bar' | 'horizontal-bar' | 'pie' | 'donut' | 'line' | 'wordcloud')[];
  options?: { value: string; label: string }[];
}

const NON_CHARTABLE_TYPES = [
  'text', 'email', 'phone', 'textarea',
  'short_text', 'long_text'
];
const PII_PATTERNS = [/email/i, /phone/i, /name/i, /address/i, /ssn/i, /password/i];

function getQuestionLabel(q: Question): string {
  return q.label || q.question || q.text || q.title || q.id || 'Untitled';
}

/**
 * Get list of fields that can be visualized as charts
 * By default excludes PII and free-text fields.
 * Pass includeAll: true to include all questions (e.g. for word cloud support).
 */
export function getChartableFields(questions: Question[], opts?: { includeAll?: boolean }): ChartableField[] {
  if (!questions || !Array.isArray(questions)) {
    return [];
  }

  return questions
    .filter(q => {
      if (opts?.includeAll) return true;

      if (NON_CHARTABLE_TYPES.includes(q.type)) return false;

      const label = getQuestionLabel(q);
      if (PII_PATTERNS.some(pattern => pattern.test(label))) return false;
      
      return true;
    })
    .map(q => ({
      id: q.id,
      label: getQuestionLabel(q),
      shortLabel: q.shortLabel,
      description: q.description,
      suggestedChartTypes: getSuggestedChartTypes(q.type),
      options: q.options
    }));
}

/**
 * Get recommended chart types based on field type.
 * Handles both legacy types (select, radio) and wizard types (single_select, multi_select, nps).
 */
function getSuggestedChartTypes(type: string): ChartableField['suggestedChartTypes'] {
  switch (type) {
    case 'rating':
    case 'number':
    case 'numeric':
    case 'nps':
      return ['bar', 'horizontal-bar', 'line'];
    case 'select':
    case 'radio':
    case 'single_select':
      return ['pie', 'donut', 'bar'];
    case 'multiselect':
    case 'checkbox':
    case 'multi_select':
      return ['horizontal-bar', 'bar'];
    case 'date':
      return ['line', 'bar'];
    case 'text':
    case 'textarea':
    case 'short_text':
    case 'long_text':
      return ['wordcloud'];
    default:
      return ['bar', 'pie'];
  }
}

const SELECT_DEMOGRAPHICS = new Set(['age_range', 'party_affiliation', 'gender']);
const DEMO_OPTIONS: Record<string, { value: string; label: string }[]> = {
  age_range: AGE_RANGE_OPTIONS,
  party_affiliation: PARTY_AFFILIATION_OPTIONS,
  gender: GENDER_OPTIONS,
};
const demoFieldLookup = new Map(DEMOGRAPHIC_FIELDS.map(f => [f.id, f]));

/**
 * Build Question[] entries for enabled demographic fields.
 *
 * District expands into one entry per part (US House, State Senate, State
 * House, County, Municipality) rather than being skipped. It used to be
 * filtered out here as a "complex nested object handled separately" — it was
 * never handled separately anywhere, so an outreach that collected district
 * could not show it as a column, chart it, or filter a report by it, even
 * though the backend has had district filtering all along.
 *
 * The parts are single_select so they chart. Their options are discovered from
 * the responses by callers that need them, since the value set is whatever
 * districts actually replied.
 */
export function buildDemographicQuestions(definitionJson: any): Question[] {
  if (!definitionJson?.demographics?.enabled) return [];

  const allFieldIds: string[] = [
    ...(definitionJson.demographics.required_fields || []),
    ...(definitionJson.demographics.optional_fields || [])
  ];

  return allFieldIds.flatMap((fieldId): Question[] => {
    if (fieldId === 'district') {
      return DISTRICT_PARTS.map(part => ({
        id: `demo_district.${part.key}`,
        label: part.label,
        shortLabel: part.label,
        description: part.label,
        type: 'single_select',
        options: [],
      }));
    }

    const fieldDef = demoFieldLookup.get(fieldId);
    const displayName =
      fieldDef?.label || fieldId.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    return [{
      id: `demo_${fieldId}`,
      label: displayName,
      shortLabel: displayName,
      description: displayName,
      type: SELECT_DEMOGRAPHICS.has(fieldId) ? 'single_select' : 'text',
      options: DEMO_OPTIONS[fieldId] || [],
    }];
  });
}
